import { useStore, useSyncStatus } from "@mapcraft-tasks/client-store/react";
import { type ReactNode, useState, useSyncExternalStore } from "react";
import { getSession, probeSession, type SessionState, signIn, subscribeSession } from "./session";

/**
 * Что показать вместо приложения — и показывать ли вместо вовсе.
 *
 * Гейт спрашивает не «есть ли интернет», а «известна ли реплика»: про сеть экраны
 * по-прежнему не знают ничего, единственный её индикатор — `SyncBadge` в сайдбаре.
 */

function useSessionState(): SessionState {
	return useSyncExternalStore(subscribeSession, getSession, getSession);
}

export function SessionGate({ children }: { children: ReactNode }) {
	const session = useSessionState();
	const status = useSyncStatus();
	const store = useStore();

	// Форма — только когда сервер честно сказал «не вошёл».
	if (session === "anonymous") {
		return <LoginScreen onSuccess={() => store.resubscribe()} />;
	}

	// Сессия истекла на живом приложении: форма ПОВЕРХ, работа и очередь на месте.
	// Ничего не чистим — сделанное оффлайн ждёт входа, а не удаления (offline-sync §12).
	if (session === "expired") {
		return (
			<>
				{children}
				<LoginScreen overlay onSuccess={() => store.resubscribe()} />
			</>
		);
	}

	// Показывать нечего и взять неоткуда.
	if (session === "unknown" && !status.loading && !status.hasReplica) {
		return (
			<NoConnection
				onRetry={async () => {
					await Promise.all([probeSession(), store.sync()]);
				}}
			/>
		);
	}

	// В том числе `probing`: рисуем скелет приложения, иначе на медленной сети между
	// «синк завершился» и «`/auth/me` ответил» мелькнул бы экран «Нет связи».
	return <>{children}</>;
}

function NoConnection({ onRetry }: { onRetry: () => Promise<void> }) {
	const [busy, setBusy] = useState(false);

	return (
		<div className="grid h-full place-items-center bg-app">
			<div className="popover w-80 p-6">
				<div className="mb-2 text-lg font-bold text-ink-strong">Нет связи</div>
				{/* Не «первый вход»: этот же экран увидит человек, у которого реплику снесло
				    по несовпадению отпечатка, и «первый вход» он прочитал бы как
				    «мои данные пропали». */}
				<p className="text-xs font-light text-ink-faint">
					Чтобы загрузить данные, нужен интернет — дальше приложение работает без него.
				</p>
				<button
					type="button"
					disabled={busy}
					className="btn btn-primary mt-4 w-full justify-center"
					onClick={async () => {
						setBusy(true);
						try {
							await onRetry();
						} finally {
							setBusy(false);
						}
					}}
				>
					Повторить
				</button>
			</div>
		</div>
	);
}

/**
 * `overlay` — та же форма поверх работающего приложения. Перезагрузки страницы здесь нет
 * намеренно: она стёрла бы фокус, скролл и открытый редактор ровно в тот момент, когда
 * человек только что доказал, что он тут. Куку браузер применит к следующему же запросу,
 * а мёртвый после 401 `EventSource` поднимает `store.resubscribe()`.
 */
function LoginScreen({ onSuccess, overlay = false }: { onSuccess: () => void; overlay?: boolean }) {
	const [password, setPassword] = useState("");
	const [error, setError] = useState<"wrong-password" | "offline" | null>(null);
	const [busy, setBusy] = useState(false);

	return (
		<div
			className={
				overlay
					? "fixed inset-0 z-50 grid place-items-center bg-app/80"
					: "grid h-full place-items-center bg-app"
			}
		>
			<form
				className="popover w-80 p-6"
				onSubmit={async (e) => {
					e.preventDefault();
					setBusy(true);
					try {
						const result = await signIn(password);
						if (result === "ok") {
							onSuccess();
							return;
						}
						setError(result);
						if (result === "wrong-password") {
							setPassword("");
						}
					} finally {
						// В `finally`: иначе отвалившаяся сеть оставила бы кнопку
						// заблокированной навсегда.
						setBusy(false);
					}
				}}
			>
				<div className="mb-6 text-lg font-bold text-ink-strong">Tasks</div>
				{overlay && (
					<p className="mb-4 text-xs font-light text-ink-faint">
						Сессия истекла — войдите заново. Всё, что вы сделали, на месте.
					</p>
				)}
				<label className="block">
					<span className="section-label">Пароль</span>
					<input
						type="password"
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
							setError(null);
						}}
						className="field mt-1 w-full"
						autoFocus
					/>
				</label>
				{error && (
					<p className="mt-2 text-xs font-light text-danger">
						{error === "wrong-password" ? "не подошёл" : "нет связи"}
					</p>
				)}
				<button
					type="submit"
					disabled={busy || password === ""}
					className="btn btn-primary mt-4 w-full justify-center"
				>
					Войти
				</button>
				{/* Сессия живёт год: PWA после недели оффлайна не должна разлогиниваться. */}
				<p className="mt-4 text-xs font-light text-ink-faint">сессия — на год</p>
			</form>
		</div>
	);
}
